import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { cartLineKey } from '../cart/cartLineKey'
import { useCart } from '../cart/CartProvider'
import { listProductTypes, type ProductTypeDto } from '../api/productTypes'
import { listProductTypesL0, type ProductTypeL0Option } from '../api/reference'
import { listMySuppliers } from '../api/mySuppliers'
import {
  listRestaurantCatalogFilterOptions,
  searchRestaurantCatalog,
  type RestaurantCatalogOfferDto,
  type RestaurantCatalogSearchPageDto,
} from '../api/restaurantCatalog'
import { listRestaurantPrivateSuppliers } from '../api/restaurantMyPrices'
import { matchDeterminationLabel } from '../api/catalogImportTypes'
import { billingErrorMessage, isFeatureNotAvailableError } from '../api/apiErrorCodes'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { useAuth } from '../auth/AuthProvider'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { usePlanGate } from '../hooks/usePlanGate'
import { useTableColumnPrefs, type TableColumnDef } from '../hooks/useTableColumnPrefs'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { useToast } from '../components/ToastProvider'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { FilterCombobox } from '../components/FilterCombobox'
import { SelectCombobox, type SelectOption } from '../components/SelectCombobox'
import { FilterChips } from '../components/FilterChips'
import { DataTable } from '../components/DataTable'
import { ResizableTh } from '../components/ResizableTh'
import { TableColumnSettings } from '../components/TableColumnSettings'
import {
  catalogSupplierKey,
  defaultCatalogFilters,
  emptyCatalogFilters,
  expandProductTypeToDescendantIds,
  findProductTypeByName,
  findProductTypeL0ByName,
  parseCatalogSupplierKey,
  resolveProductTypeCascade,
  getEffectiveProductTypeId,
  formatProductTypeLabel,
  nameQueryTags,
  buildCatalogFilterChips,
  clearCatalogFilterChip,
  type CatalogFilters,
} from './catalogFilters'

const BUYER_CATALOG_COLUMNS: TableColumnDef[] = [
  {
    id: 'cart',
    label: 'В корзине',
    required: true,
    pin: 'start',
    hideInSettings: true,
    defaultWidth: 48,
    minWidth: 40,
  },
  { id: 'name', label: 'Позиция', required: true, defaultWidth: 260, minWidth: 120 },
  { id: 'price', label: 'Цена', defaultWidth: 100, minWidth: 72 },
  { id: 'unit', label: 'Ед. изм.', defaultWidth: 88, minWidth: 64 },
  { id: 'brand', label: 'Бренд', defaultWidth: 120, minWidth: 72 },
  { id: 'country', label: 'Страна', defaultWidth: 110, minWidth: 72 },
  { id: 'category', label: 'Категория', defaultWidth: 160, minWidth: 96 },
  { id: 'supplier', label: 'Поставщик', defaultWidth: 180, minWidth: 96 },
  {
    id: 'package',
    label: 'Фасовка',
    defaultVisible: false,
    defaultWidth: 110,
    minWidth: 72,
  },
  {
    id: 'minShipment',
    label: 'Мин. отгрузка',
    defaultVisible: false,
    defaultWidth: 110,
    minWidth: 72,
  },
  {
    id: 'expand',
    label: 'Подробнее',
    required: true,
    pin: 'end',
    hideInSettings: true,
    defaultWidth: 48,
    minWidth: 40,
  },
]

export const CatalogScreen = () => {
  const { session } = useAuth()
  const entitlements = useBuyerEntitlements()
  const { requestFeature, paywallModal } = usePlanGate()
  const myPricesOnlyDefault = !entitlements.marketCatalogEnabled
  const navigate = useNavigate()
  const { addOffer, hasLine, lineCount, removeLine } = useCart()
  const { showToast } = useToast()
  const columnPrefs = useTableColumnPrefs('buyer:catalog', BUYER_CATALOG_COLUMNS)
  const [filters, setFilters] = useState(() => defaultCatalogFilters(myPricesOnlyDefault))
  const [draft, setDraft] = useState(() => defaultCatalogFilters(myPricesOnlyDefault))
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [productTypesL0, setProductTypesL0] = useState<ProductTypeL0Option[]>([])
  const [countryOptions, setCountryOptions] = useState<string[]>([])
  const [brandOptions, setBrandOptions] = useState<string[]>([])
  const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([])
  const [typesLoading, setTypesLoading] = useState(true)
  const [page, setPage] = useState<RestaurantCatalogSearchPageDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => new Set())

  const toggleRowDetails = useCallback((rowId: string) => {
    setExpandedRows((current) => {
      const next = new Set(current)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }, [])

  useEffect(() => {
    const onlyMyPrices = !entitlements.marketCatalogEnabled
    setFilters(defaultCatalogFilters(onlyMyPrices))
    setDraft(defaultCatalogFilters(onlyMyPrices))
  }, [entitlements.marketCatalogEnabled])

  // Поиск категории по всем уровням: можно вписать сразу «карбонад» / «вырезка»,
  // не только отдел L0. Выбранный узел раскладывается по L0/L1/L2 для фильтра.
  const categorySearchOptions = useMemo(() => {
    const seen = new Set<string>()
    const names: string[] = []
    for (const type of [...productTypes].sort((l, r) => l.name.localeCompare(r.name, 'ru'))) {
      if (type.active === false || !type.code?.trim() || seen.has(type.name)) {
        continue
      }
      seen.add(type.name)
      names.push(type.name)
    }
    return names
  }, [productTypes])

  const typeNames = useMemo(() => {
    const map = new Map<number, string>()
    for (const type of productTypes) {
      map.set(type.id, type.name)
    }
    return map
  }, [productTypes])

  const nameSearchActive = nameQueryTags(filters.nameQuery).length > 0

  const loadSearch = useCallback(
    async (nextFilters: CatalogFilters) => {
      if (!session) {
        return
      }
      setLoading(true)
      setError(null)
      try {
        const tags = nameQueryTags(nextFilters.nameQuery)
        const effectiveProductTypeId = getEffectiveProductTypeId(nextFilters)
        const supplierRef = parseCatalogSupplierKey(nextFilters.supplierKey)
        const result = await searchRestaurantCatalog({
          organizationId: session.organizationId,
          productTypeIds: effectiveProductTypeId
            ? expandProductTypeToDescendantIds(effectiveProductTypeId, productTypes)
            : undefined,
          country: nextFilters.country,
          brand: nextFilters.brand,
          minPrice: nextFilters.minPrice,
          maxPrice: nextFilters.maxPrice,
          customTags: tags,
          myPricesOnly: nextFilters.myPricesOnly,
          page: nextFilters.page,
          ...(supplierRef?.kind === 'org' ? { supplierOrganizationId: supplierRef.id } : {}),
          ...(supplierRef?.kind === 'private' ? { privateSupplierId: supplierRef.id } : {}),
          ...(nextFilters.matchStatus.trim() ? { matchStatus: nextFilters.matchStatus.trim() } : {}),
          unassignedProductType: nextFilters.unassignedOnly,
        })

        setPage(result)
        setFilters(nextFilters)
      } catch (loadError) {
        if (isFeatureNotAvailableError(loadError)) {
          requestFeature('market_catalog')
          setError(billingErrorMessage(loadError))
          return
        }
        setError(formatApiErrorMessage(loadError, 'Не удалось выполнить поиск'))
      } finally {
        setLoading(false)
      }
    },
    [session, productTypes, requestFeature],
  )

  useEffect(() => {
    void (async () => {
      try {
        const [types, l0Types] = await Promise.all([listProductTypes(), listProductTypesL0()])
        setProductTypes(types)
        setProductTypesL0(l0Types)
      } catch {
        setError('Не удалось загрузить категории товаров')
      } finally {
        setTypesLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    if (!session) {
      return
    }
    void listRestaurantCatalogFilterOptions(session.organizationId, filters.myPricesOnly)
      .then((options) => {
        setCountryOptions(options.countries)
        setBrandOptions(options.brands)
      })
      .catch(() => undefined)
    void Promise.all([
      listMySuppliers(session.organizationId),
      listRestaurantPrivateSuppliers(session.organizationId),
    ])
      .then(([orgPage, privateList]) => {
        const options: SelectOption[] = [{ value: '', label: 'Все' }]
        for (const item of orgPage.items) {
          if (!item.supplierName.trim()) {
            continue
          }
          options.push({
            value: catalogSupplierKey('org', item.supplierOrganizationId),
            label: item.supplierName,
          })
        }
        for (const item of privateList) {
          const label = (item.displayLabel || item.legalName).trim()
          if (!label) {
            continue
          }
          options.push({
            value: catalogSupplierKey('private', item.id),
            label,
          })
        }
        setSupplierOptions(options)
      })
      .catch(() => undefined)
  }, [session, filters.myPricesOnly])

  useEffect(() => {
    if (!session || typesLoading) {
      return
    }
    void loadSearch(defaultCatalogFilters(!entitlements.marketCatalogEnabled))
  }, [session, typesLoading, loadSearch, entitlements.marketCatalogEnabled])

  const handleApply = () => {
    void loadSearch({ ...draft, page: 0 })
  }

  const handleReset = () => {
    const reset = defaultCatalogFilters(myPricesOnlyDefault)
    setDraft(reset)
    void loadSearch(reset)
  }

  const defaultFilters = useMemo(
    () => defaultCatalogFilters(myPricesOnlyDefault),
    [myPricesOnlyDefault],
  )

  const activeFilterChips = useMemo(
    () => buildCatalogFilterChips(filters, defaultFilters),
    [filters, defaultFilters],
  )

  const handleRemoveFilterChip = (chipId: string) => {
    const nextFilters = clearCatalogFilterChip(filters, chipId, defaultFilters)
    setDraft(nextFilters)
    void loadSearch({ ...nextFilters, page: 0 })
  }

  const handleToggleCart = (offer: RestaurantCatalogOfferDto) => {
    const key = cartLineKey({
      supplierKind: offer.isRegisteredSupplier ? 'registered' : 'external',
      supplierOrganizationId: offer.supplierOrganizationId,
      privateSupplierId: offer.privateSupplierId,
      catalogItemId: offer.itemId,
    })
    if (hasLine(key)) {
      removeLine(key)
      showToast(`«${offer.itemName}» убрано из корзины`)
      return
    }
    if (offer.productTypeId == null) {
      showToast('Нельзя добавить позицию без категории — сначала назначьте тип в «Мои прайсы»')
      return
    }
    addOffer({
      offer: { ...offer, productTypeId: offer.productTypeId },
      productTypeName: formatProductTypeLabel(
        offer.productTypeId,
        offer.productTypeName,
        offer.productTypeActive,
        typeNames,
      ),
    })
    showToast(`«${offer.itemName}» добавлено в корзину`)
  }

  return (
    <section className="market-page market-page--wide">
      <MarketPageHeader
        title={myPricesOnlyDefault ? 'Поиск по моим прайсам' : 'Каталог поставщиков'}
        description={
          myPricesOnlyDefault
            ? 'На тарифе «Старт» доступен поиск только по позициям из «Моих прайсов». Добавляйте их в корзину и формируйте бланк заказа.'
            : 'Сравните предложения по категориям и добавьте лучшие в корзину.'
        }
        actions={
          lineCount > 0 ? (
            <>
              <button type="button" className="echat-btn echat-btn--ghost" onClick={() => navigate('/cart')}>
                Корзина ({lineCount})
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                onClick={() => {
                  if (requestFeature('platform_rfq')) {
                    navigate('/orders/create')
                  }
                }}
              >
                Сформировать заявки
              </button>
            </>
          ) : undefined
        }
      />

      {paywallModal}

      <div className="market-page__filter-bar">
        <label className="market-filters__checkbox market-page__scope-toggle">
          <input
            type="checkbox"
            checked={draft.myPricesOnly}
            onChange={(event) => {
              if (entitlements.isStartPlan && !event.target.checked) {
                requestFeature('market_catalog')
                return
              }
              setDraft((current) => ({ ...current, myPricesOnly: event.target.checked }))
            }}
          />
          <span>Искать только в «Моих прайсах»</span>
        </label>
        <button
          type="button"
          className="market-filters__more-toggle"
          aria-expanded={showMoreFilters}
          onClick={() => setShowMoreFilters((value) => !value)}
        >
          {showMoreFilters ? 'Скрыть фильтры' : 'Ещё фильтры'}
          <svg
            className={`market-filters__more-chevron${showMoreFilters ? ' market-filters__more-chevron--open' : ''}`}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <form
        className="market-filters market-filters--catalog"
        onSubmit={(event) => {
          event.preventDefault()
          handleApply()
        }}
      >
        <div className="market-filters__row market-filters__row--search">
          <label className="echat-field market-filters__field market-filters__field--grow">
            <span className="echat-field__label">Название</span>
            <input
              className="echat-field__input"
              value={draft.nameQuery}
              placeholder="Часть слова в названии"
              onChange={(event) => setDraft((current) => ({ ...current, nameQuery: event.target.value }))}
            />
          </label>

          <FilterCombobox
            className="market-filters__field market-filters__field--grow"
            label="Категория"
            value={draft.productTypeL0Query}
            options={categorySearchOptions}
            placeholder="Мясо, вырезка, карбонад…"
            listAllOnFocus
            maxOptions={200}
            onChange={(value) => {
              // Точное совпадение по любому уровню → раскладываем путь в фильтр.
              const exact = findProductTypeByName(productTypes, value)
              if (exact) {
                const cascade = resolveProductTypeCascade(exact.id, productTypes)
                const nameOf = (id: number | null) =>
                  id == null ? '' : (productTypes.find((type) => type.id === id)?.name ?? '')
                // Самый глубокий уровень ниже L1 — точный лист приоритетнее предка.
                const deepestId = cascade.l3Id ?? cascade.l2Id ?? null
                setDraft((current) => ({
                  ...current,
                  productTypeL0Query: value,
                  productTypeL0Id: cascade.l0Id,
                  productTypeL1Query: nameOf(cascade.l1Id),
                  productTypeL1Id: cascade.l1Id,
                  productTypeL2Query: nameOf(deepestId),
                  productTypeL2Id: deepestId,
                }))
                return
              }
              // Частичный ввод — оставляем как запрос L0 (совместимо со старым поведением).
              const l0Match = findProductTypeL0ByName(productTypesL0, value)
              setDraft((current) => ({
                ...current,
                productTypeL0Query: value,
                productTypeL0Id: l0Match?.id ?? null,
                productTypeL1Query: '',
                productTypeL1Id: null,
                productTypeL2Query: '',
                productTypeL2Id: null,
              }))
            }}
          />

          <div className="market-filters__actions">
            <span className="echat-field__label market-filters__actions-label" aria-hidden="true">
              &nbsp;
            </span>
            <div className="market-filters__actions-row">
              <TableColumnSettings prefs={columnPrefs} />
              <button type="submit" className="echat-btn echat-btn--primary">
                Найти
              </button>
              <button type="button" className="echat-btn echat-btn--ghost" onClick={handleReset}>
                Сбросить
              </button>
            </div>
          </div>
        </div>

        <div
          className={`market-filters__secondary-row market-filters__collapsible${
            showMoreFilters ? ' market-filters__collapsible--open' : ''
          }`}
          hidden={!showMoreFilters}
        >
          <label className="echat-field market-filters__field">
            <span className="echat-field__label">Цена от</span>
            <input
              className="echat-field__input"
              inputMode="decimal"
              value={draft.minPrice}
              onChange={(event) => setDraft((current) => ({ ...current, minPrice: event.target.value }))}
            />
          </label>

          <label className="echat-field market-filters__field">
            <span className="echat-field__label">Цена до</span>
            <input
              className="echat-field__input"
              inputMode="decimal"
              value={draft.maxPrice}
              onChange={(event) => setDraft((current) => ({ ...current, maxPrice: event.target.value }))}
            />
          </label>

          <FilterCombobox
            className="market-filters__field"
            label="Страна"
            value={draft.country}
            options={countryOptions}
            placeholder="Начните вводить"
            onChange={(country) => setDraft((current) => ({ ...current, country }))}
          />

          <FilterCombobox
            className="market-filters__field"
            label="Бренд"
            value={draft.brand}
            options={brandOptions}
            placeholder="Начните вводить"
            onChange={(brand) => setDraft((current) => ({ ...current, brand }))}
          />

          <SelectCombobox
            className="market-filters__field"
            label="Поставщик"
            value={draft.supplierKey}
            options={supplierOptions}
            placeholder="Все"
            onChange={(supplierKey) => {
              const selected = supplierOptions.find((option) => option.value === supplierKey)
              setDraft((current) => ({
                ...current,
                supplierKey,
                supplierLabel: selected?.label && supplierKey ? selected.label : '',
              }))
            }}
          />

          <SelectCombobox
            className="market-filters__field market-filters__field--match-status"
            label="Тип определения"
            value={draft.matchStatus}
            options={[
              { value: '', label: 'Все' },
              { value: 'auto', label: matchDeterminationLabel('auto') },
              { value: 'suggested', label: matchDeterminationLabel('suggested') },
              { value: 'manual', label: matchDeterminationLabel('manual') },
              { value: 'unmatched', label: matchDeterminationLabel('unmatched') },
            ]}
            placeholder="Все"
            onChange={(matchStatus) => setDraft((current) => ({ ...current, matchStatus }))}
          />

          <label className="market-filters__checkbox market-filters__checkbox--inline market-filters__checkbox--secondary">
            <input
              type="checkbox"
              checked={draft.unassignedOnly}
              onChange={(event) => {
                const unassignedOnly = event.target.checked
                setDraft((current) => ({
                  ...current,
                  unassignedOnly,
                  ...(unassignedOnly
                    ? {
                        productTypeL0Id: null,
                        productTypeL0Query: '',
                        productTypeL1Id: null,
                        productTypeL1Query: '',
                        productTypeL2Id: null,
                        productTypeL2Query: '',
                      }
                    : {}),
                }))
              }}
            />
            <span>Только без категории</span>
          </label>
        </div>
      </form>

      <FilterChips
        chips={activeFilterChips}
        onRemove={handleRemoveFilterChip}
        onClearAll={handleReset}
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && page?.interpretation ? (
        <article className="echat-panel market-catalog-summary">
          <p className="echat-panel__text">
            Найдено {page.totalElements} позиций от {page.interpretation.suppliersCount} поставщиков
            {nameSearchActive ? ' (поиск по названию)' : null}
          </p>
          {page.interpretation.cheapestPrice && page.interpretation.highestPrice ? (
            <p className="echat-muted">
              Цены: {page.interpretation.cheapestPrice} — {page.interpretation.highestPrice}
            </p>
          ) : null}
          {page.interpretation.note ? (
            <p className="echat-muted">{page.interpretation.note}</p>
          ) : null}
        </article>
      ) : null}

      {!loading && page && page.items.length === 0 ? (
        <MarketEmptyState
          title="Предложений не найдено"
          description="По выбранным фильтрам товаров нет. Попробуйте другую категорию или сбросьте фильтры."
          actions={[
            { label: 'Сбросить фильтры', onClick: handleReset, variant: 'ghost' },
            ...(myPricesOnlyDefault
              ? [{ label: 'Мои прайсы', to: '/my-prices' as const }]
              : [{ label: 'Все категории', onClick: () => void loadSearch(emptyCatalogFilters()) }]),
          ]}
        />
      ) : null}

      {page && page.items.length > 0 ? (
        <DataTable className="market-table--catalog market-table--cards market-table--configurable" wrapClassName="market-table-wrap--cards" caption="Каталог поставщиков">
            <colgroup>
              {columnPrefs.visibleColumns.map((column) => (
                <col
                  key={column.id}
                  style={
                    columnPrefs.getWidth(column.id) != null
                      ? { width: columnPrefs.getWidth(column.id) }
                      : undefined
                  }
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columnPrefs.visibleColumns.map((column) => (
                  <ResizableTh
                    key={column.id}
                    columnId={column.id}
                    label={column.label}
                    width={columnPrefs.getWidth(column.id)}
                    minWidth={columnPrefs.getMinWidth(column.id)}
                    className={
                      column.id === 'cart'
                        ? 'market-table__col--check'
                        : column.id === 'expand'
                          ? 'market-table__col--expand'
                          : undefined
                    }
                    onResize={columnPrefs.setWidth}
                  >
                    {column.id === 'cart' || column.id === 'expand' ? null : column.label}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {page.items.map((offer) => {
                const rowId = `${offer.itemId}-${offer.privateSupplierId ?? offer.supplierOrganizationId ?? 'pub'}`
                const inCart = hasLine(
                  cartLineKey({
                    supplierKind: offer.isRegisteredSupplier ? 'registered' : 'external',
                    supplierOrganizationId: offer.supplierOrganizationId,
                    privateSupplierId: offer.privateSupplierId,
                    catalogItemId: offer.itemId,
                  }),
                )
                const expanded = expandedRows.has(rowId)
                const categoryLabel = formatProductTypeLabel(
                  offer.productTypeId,
                  offer.productTypeName,
                  offer.productTypeActive,
                  typeNames,
                )
                return (
                  <Fragment key={rowId}>
                    <tr
                      className={
                        inCart
                          ? 'market-catalog-row market-table__row--selected'
                          : 'market-catalog-row'
                      }
                      onClick={() => handleToggleCart(offer)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleToggleCart(offer)
                        }
                      }}
                      tabIndex={0}
                      aria-selected={inCart}
                      aria-label={`${offer.itemName}, ${inCart ? 'в корзине' : 'не в корзине'}`}
                    >
                      {columnPrefs.visibleColumns.map((column) => {
                        switch (column.id) {
                          case 'cart':
                            return (
                              <td
                                key={column.id}
                                className="market-table__col--check"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={inCart}
                                  onChange={() => handleToggleCart(offer)}
                                  aria-label={`${inCart ? 'Убрать' : 'Добавить'} «${offer.itemName}» в корзину`}
                                />
                              </td>
                            )
                          case 'name':
                            return (
                              <td key={column.id} data-label="Позиция" className="market-table__cell--primary">
                                {offer.itemName}
                              </td>
                            )
                          case 'price':
                            return (
                              <td key={column.id} data-label="Цена" className="market-table__cell--price">
                                {offer.price ?? '—'}
                              </td>
                            )
                          case 'unit':
                            return (
                              <td key={column.id} data-label="Ед. изм.">
                                {offer.unit ? formatUnitLabel(offer.unit) : '—'}
                              </td>
                            )
                          case 'brand':
                            return (
                              <td key={column.id} data-label="Бренд">
                                {offer.brand ?? '—'}
                              </td>
                            )
                          case 'country':
                            return (
                              <td key={column.id} data-label="Страна">
                                {offer.country ?? '—'}
                              </td>
                            )
                          case 'category':
                            return (
                              <td key={column.id} data-label="Категория">
                                {categoryLabel}
                              </td>
                            )
                          case 'supplier':
                            return (
                              <td key={column.id} data-label="Поставщик">
                                {offer.supplierOrganizationName}
                              </td>
                            )
                          case 'package':
                            return (
                              <td key={column.id} data-label="Фасовка">
                                {offer.packageSize ?? '—'}
                              </td>
                            )
                          case 'minShipment':
                            return (
                              <td key={column.id} data-label="Мин. отгрузка">
                                {offer.minShipmentQty ?? '—'}
                              </td>
                            )
                          case 'expand':
                            return (
                              <td
                                key={column.id}
                                className="market-table__col--expand"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className={`market-table__expand-btn${expanded ? ' market-table__expand-btn--open' : ''}`}
                                  aria-expanded={expanded}
                                  aria-label={expanded ? 'Скрыть детали' : 'Показать детали'}
                                  onClick={() => toggleRowDetails(rowId)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              </td>
                            )
                          default:
                            return null
                        }
                      })}
                    </tr>
                    {expanded ? (
                      <tr className="market-table__detail-row">
                        <td colSpan={columnPrefs.visibleCount}>
                          <dl className="market-detail-grid">
                            <div className="market-detail-grid__item">
                              <dt>Бренд</dt>
                              <dd>{offer.brand ?? '—'}</dd>
                            </div>
                            <div className="market-detail-grid__item">
                              <dt>Страна</dt>
                              <dd>{offer.country ?? '—'}</dd>
                            </div>
                            <div className="market-detail-grid__item">
                              <dt>Категория</dt>
                              <dd>{categoryLabel}</dd>
                            </div>
                          </dl>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
        </DataTable>
      ) : null}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <span>
            Страница {page.page + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={filters.page <= 0}
            onClick={() => void loadSearch({ ...filters, page: filters.page - 1 })}
          >
            ←
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={page.page + 1 >= page.totalPages}
            onClick={() => void loadSearch({ ...filters, page: filters.page + 1 })}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
